import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Puzzle, ArrowLeft, Trophy, Clock, Star, 
  RotateCcw, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const WordPuzzleGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const difficulty = searchParams.get('difficulty') || 'easy';
  
  const [gameState, setGameState] = useState('ready');
  const [grid, setGrid] = useState([]);
  const [words, setWords] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [timer, setTimer] = useState(0);
  const [score, setScore] = useState(0);

  const wordLists = {
    easy: {
      words: ['CAT', 'DOG', 'SUN', 'HAT', 'PEN'],
      gridSize: 8
    },
    medium: {
      words: ['HOUSE', 'WATER', 'PLANT', 'HAPPY', 'SCHOOL', 'FRIEND'],
      gridSize: 10
    },
    hard: {
      words: ['MATHEMATICS', 'SCIENCE', 'ENGLISH', 'GEOGRAPHY', 'HISTORY', 'COMPUTER'],
      gridSize: 12
    }
  };

  const generateGrid = useCallback(() => {
    const config = wordLists[difficulty];
    const size = config.gridSize;
    const wordsToFind = config.words;
    
    // Create empty grid
    const newGrid = Array(size).fill(null).map(() => 
      Array(size).fill(null).map(() => ({
        letter: '',
        isPartOfWord: false,
        wordId: null
      }))
    );
    
    // Place words
    const placedWords = [];
    const directions = [
      [0, 1],   // horizontal
      [1, 0],   // vertical
      [1, 1],   // diagonal down-right
    ];
    
    for (const word of wordsToFind) {
      let placed = false;
      let attempts = 0;
      
      while (!placed && attempts < 100) {
        const dir = directions[Math.floor(Math.random() * directions.length)];
        const startRow = Math.floor(Math.random() * size);
        const startCol = Math.floor(Math.random() * size);
        
        // Check if word fits
        const endRow = startRow + dir[0] * (word.length - 1);
        const endCol = startCol + dir[1] * (word.length - 1);
        
        if (endRow >= 0 && endRow < size && endCol >= 0 && endCol < size) {
          let canPlace = true;
          const cells = [];
          
          for (let i = 0; i < word.length; i++) {
            const r = startRow + dir[0] * i;
            const c = startCol + dir[1] * i;
            const existing = newGrid[r][c].letter;
            
            if (existing && existing !== word[i]) {
              canPlace = false;
              break;
            }
            cells.push({ r, c, letter: word[i] });
          }
          
          if (canPlace) {
            cells.forEach(({ r, c, letter }) => {
              newGrid[r][c] = {
                letter,
                isPartOfWord: true,
                wordId: word
              };
            });
            placedWords.push({
              word,
              cells,
              found: false
            });
            placed = true;
          }
        }
        attempts++;
      }
    }
    
    // Fill empty cells with random letters
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (!newGrid[r][c].letter) {
          newGrid[r][c].letter = alphabet[Math.floor(Math.random() * 26)];
        }
      }
    }
    
    setGrid(newGrid);
    setWords(placedWords);
  }, [difficulty]);

  const startGame = useCallback(() => {
    generateGrid();
    setGameState('playing');
    setFoundWords([]);
    setSelectedCells([]);
    setTimer(0);
    setScore(0);
  }, [generateGrid]);

  // Timer
  useEffect(() => {
    if (gameState === 'playing') {
      const interval = setInterval(() => {
        setTimer(t => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameState]);

  // Check for game completion
  useEffect(() => {
    if (gameState === 'playing' && words.length > 0 && foundWords.length === words.length) {
      endGame();
    }
  }, [foundWords, words, gameState]);

  const endGame = async () => {
    setGameState('finished');
    
    const basePoints = foundWords.length * 15;
    const timeBonus = Math.max(0, 100 - timer);
    const totalPoints = basePoints + timeBonus;
    
    try {
      await api.post('/games/complete', {
        game_id: 'word-puzzle',
        score: totalPoints,
        time_seconds: timer
      });
      toast.success(`🎉 You earned ${totalPoints} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
    
    setScore(totalPoints);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCellMouseDown = (row, col) => {
    if (gameState !== 'playing') return;
    setIsSelecting(true);
    setSelectedCells([{ row, col }]);
  };

  const handleCellMouseEnter = (row, col) => {
    if (!isSelecting || gameState !== 'playing') return;
    
    // Only allow straight lines
    if (selectedCells.length > 0) {
      const first = selectedCells[0];
      const last = selectedCells[selectedCells.length - 1];
      
      // Check if new cell is in line with selection
      const rowDiff = row - first.row;
      const colDiff = col - first.col;
      
      if (rowDiff === 0 || colDiff === 0 || Math.abs(rowDiff) === Math.abs(colDiff)) {
        // Valid direction - add all cells in between
        const newCells = [];
        const steps = Math.max(Math.abs(rowDiff), Math.abs(colDiff));
        const rowStep = steps ? rowDiff / steps : 0;
        const colStep = steps ? colDiff / steps : 0;
        
        for (let i = 0; i <= steps; i++) {
          newCells.push({
            row: first.row + Math.round(rowStep * i),
            col: first.col + Math.round(colStep * i)
          });
        }
        setSelectedCells(newCells);
      }
    }
  };

  const handleCellMouseUp = () => {
    if (!isSelecting || gameState !== 'playing') return;
    setIsSelecting(false);
    
    // Check if selected cells form a word
    const selectedWord = selectedCells.map(({ row, col }) => grid[row][col].letter).join('');
    const reversedWord = selectedWord.split('').reverse().join('');
    
    const foundWord = words.find(w => 
      !foundWords.includes(w.word) && 
      (w.word === selectedWord || w.word === reversedWord)
    );
    
    if (foundWord) {
      setFoundWords([...foundWords, foundWord.word]);
      // Highlight found cells
      const newGrid = [...grid];
      selectedCells.forEach(({ row, col }) => {
        newGrid[row][col] = { ...newGrid[row][col], found: true };
      });
      setGrid(newGrid);
    }
    
    setSelectedCells([]);
  };

  // Touch handlers for mobile
  const handleTouchStart = (row, col, e) => {
    e.preventDefault();
    handleCellMouseDown(row, col);
  };

  const handleTouchMove = (e) => {
    if (!isSelecting) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row && element.dataset.col) {
      const row = parseInt(element.dataset.row);
      const col = parseInt(element.dataset.col);
      handleCellMouseEnter(row, col);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleCellMouseUp();
  };

  const isCellSelected = (row, col) => {
    return selectedCells.some(c => c.row === row && c.col === col);
  };

  return (
    <StudentLayout>
      <div data-testid="word-puzzle-game" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full">
                <Clock className="w-5 h-5 text-pink-600" />
                <span className="font-bold text-pink-700">{formatTime(timer)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Header */}
        <div className="student-card bg-pink-100 border-pink-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-pink-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                <Puzzle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Word Puzzle</h1>
                <p className="text-slate-600">Find all hidden words!</p>
              </div>
            </div>
            {gameState === 'playing' && (
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Restart
              </Button>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-pink-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <Puzzle className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Word Search</h2>
            <p className="text-lg text-slate-600 mb-8">
              Find hidden words in the grid!<br/>
              Words can go horizontal, vertical, or diagonal.
            </p>
            <Button onClick={startGame} className="student-btn text-lg px-10 py-6">
              Start Game
              <Puzzle className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Grid */}
            <div className="md:col-span-2 student-card">
              <div 
                className="grid gap-1 select-none touch-none"
                style={{ gridTemplateColumns: `repeat(${grid.length}, 1fr)` }}
                onMouseLeave={() => setIsSelecting(false)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {grid.map((row, rowIndex) => 
                  row.map((cell, colIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      data-row={rowIndex}
                      data-col={colIndex}
                      className={`
                        aspect-square flex items-center justify-center font-bold text-sm md:text-lg rounded cursor-pointer transition-all
                        ${cell.found ? 'bg-emerald-200 text-emerald-800' : 
                          isCellSelected(rowIndex, colIndex) ? 'bg-pink-300 text-pink-800' : 
                          'bg-slate-100 hover:bg-slate-200 text-slate-700'}
                      `}
                      onMouseDown={() => handleCellMouseDown(rowIndex, colIndex)}
                      onMouseEnter={() => handleCellMouseEnter(rowIndex, colIndex)}
                      onMouseUp={handleCellMouseUp}
                      onTouchStart={(e) => handleTouchStart(rowIndex, colIndex, e)}
                    >
                      {cell.letter}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Words List */}
            <div className="student-card">
              <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">
                Words to Find ({foundWords.length}/{words.length})
              </h3>
              <div className="space-y-2">
                {words.map(({ word }) => (
                  <div
                    key={word}
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      foundWords.includes(word) ? 'bg-emerald-100 line-through' : 'bg-slate-50'
                    }`}
                  >
                    {foundWords.includes(word) && <Check className="w-4 h-4 text-emerald-600" />}
                    <span className={foundWords.includes(word) ? 'text-emerald-700' : 'text-slate-700'}>
                      {word}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">All Words Found!</h2>
            <p className="text-xl text-slate-600 mb-6">
              Completed in {formatTime(timer)}!
            </p>
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">+{score}</span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="student-btn">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default WordPuzzleGame;
